import { AttendeeAvatar } from "#clockwise/web-commons/src/ui/AttendeeAvatar";
import { AttendeeAvatarStack } from "#clockwise/web-commons/src/ui/AttendeeAvatarStack";
import { Button, Menu, MenuGroup, MenuItem, TextArea } from "@clockwise/design-system";
import { FindTime, Search } from "@clockwise/design-system/icons";
import { fg_onEmphasis } from "@clockwise/design-system/tokens";
import { Close } from "@clockwise/icons";
import { noop } from "lodash";
import pluralize from "pluralize";
import React, { FormEventHandler } from "react";
import { Profile, getFullName } from "../../../util/profile.util";
import { ColorCheckbox } from "../../ColorCheckbox";
import { CircularProgress } from "../../circular-progress";

type SelectionState = "suggested" | "selected-active" | "selected-inactive" | "selected-fixed";
export type CalendarSelectPeopleOption = {
  calendarId: string;
  color: string;
  profile: Profile;
  selectionState: SelectionState;
};

type CalendarSelectPeopleProps = {
  message?: string;
  onAutoSchedule: () => void;
  onOpenClose: (open: boolean) => void;
  onSearchChange: (search: string) => void;
  onSelect: (person: string) => void;
  onSelectionsClear: () => void;
  onUnselect: (person: string) => void;
  options: CalendarSelectPeopleOption[];
  search: string;
  searchLoading: boolean;
};

export const CalendarSelectPeople = ({
  message = "",
  onAutoSchedule = noop,
  onOpenClose = noop,
  onSearchChange = noop,
  onSelect = noop,
  onSelectionsClear = noop,
  onUnselect = noop,
  options = [],
  search = "",
  searchLoading = false,
}: CalendarSelectPeopleProps) => {
  const searching = search.length > 0;
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const focusInput = () => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const handleOpen = () => {
    onOpenClose(true);
    focusInput();
  };

  const handleSearchChange: FormEventHandler<HTMLTextAreaElement> = (event) => {
    onSearchChange(event.currentTarget.value);
  };

  const handleSearchClear = () => {
    onSearchChange("");
    focusInput();
  };

  const handleSelect = (calendarId: string) => {
    onSelect(calendarId);
    focusInput();
  };

  const handleUnselect = (calendarId: string) => {
    onUnselect(calendarId);
    focusInput();
  };

  const handleAutoSchedule = () => {
    onAutoSchedule();
  };

  const fixedOption = options.find((option) => option.selectionState === "selected-fixed");
  const suggestedOptions = options.filter((option) => option.selectionState === "suggested");
  const activeOptions = options.filter((option) => option.selectionState === "selected-active");
  const selectedOptions = options.filter(
    (option) =>
      option.selectionState === "selected-active" || option.selectionState === "selected-inactive",
  );

  return (
    <Menu
      label={<CollapsedSelectPerson fixedOption={fixedOption} selectedOptions={selectedOptions} />}
      maxHeight="600px"
      showExpandIcon={false}
      onOpen={handleOpen}
      keepOpen
      size="small"
      variant="default"
    >
      <div className="cw-px-1">
        <TextArea
          endIcon={search.length ? Close : Search}
          onIconClick={search.length ? handleSearchClear : undefined}
          placeholder="Search for people"
          inline
          borderless
          fullWidth
          value={search}
          onChange={handleSearchChange}
          onKeyDown={(event) => {
            if (event.code === "Space") {
              // The Menu _hates_ spaces and will close onKeyDown for events with the spce key
              // This workaround prevents propogation of the space key event to the parent Menu
              // and instead appends a space to the search query
              // This does fail if the user attempts to put a space anywhere other than the end of the search query
              onSearchChange(search + " ");
              event.stopPropagation();
              event.preventDefault();
            }
          }}
          ref={inputRef}
        />
      </div>
      {message && (
        <MenuGroup>
          <div className="cw-px-1 cw-pb-2 cw-mt-4 cw-max-w-[350px] cw-body-base cw-text-12 cw-font-medium">
            {message}
          </div>
        </MenuGroup>
      )}
      <MenuGroup>
        {searching && (
          <>
            {searchLoading && <SearchLoading />}
            {!searchLoading && !suggestedOptions.length && (
              <NoResults onClear={handleSearchClear} />
            )}
          </>
        )}
        {!searchLoading && (
          <>
            {suggestedOptions.map((option, index) => (
              <SuggestedOption key={index} option={option} onClick={handleSelect} />
            ))}
          </>
        )}
      </MenuGroup>
      <MenuGroup>
        {selectedOptions.map((option, index) => (
          <SelectedOption
            checked={option.selectionState === "selected-active"}
            key={index}
            onClick={option.selectionState === "selected-active" ? handleUnselect : handleSelect}
            option={option}
          />
        ))}
        {fixedOption && <SelectedOption isFixed checked option={fixedOption} />}
      </MenuGroup>
      {activeOptions.length >= 2 && <ClearSelections onClick={onSelectionsClear} />}
      {activeOptions.length >= 1 && (
        <MenuGroup>
          <MenuItem>
            <FindATimeOption onClick={handleAutoSchedule} />
          </MenuItem>
        </MenuGroup>
      )}
    </Menu>
  );
};

const CollapsedSelectPerson = ({
  selectedOptions,
  fixedOption,
}: {
  selectedOptions: CalendarSelectPeopleOption[];
  fixedOption: CalendarSelectPeopleOption | undefined;
}) => {
  return (
    <div className="cw-flex cw-items-center cw-space-x-1">
      <div className="cw-max-h-[1em] cw-whitespace-nowrap cw-overflow-hidden cw-text-ellipsis cw-leading-[13px]">{`${
        selectedOptions.length + 1
      } ${pluralize("calendar", selectedOptions.length + 1)}`}</div>
      <AttendeeAvatarStack size="medium" maxShown={5}>
        {selectedOptions.map(({ profile, color }, index) => (
          <AttendeeAvatar key={index} size="medium" profile={profile} borderColor={color} />
        ))}
        {fixedOption && (
          <AttendeeAvatar
            size="medium"
            profile={fixedOption.profile}
            borderColor={fixedOption.color}
          />
        )}
      </AttendeeAvatarStack>
    </div>
  );
};

const SearchLoading = () => {
  return (
    <div className="cw-p-1">
      <CircularProgress variant="inline" /> Loading...
    </div>
  );
};

const NoResults = ({ onClear }: { onClear: () => void }) => {
  return (
    <div className="cw-p-1 cw-flex cw-justify-between cw-items-center">
      <div>No results</div>
      <Button variant="text" size="mini" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
};

const ClearSelections = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button variant="text" size="mini" onClick={onClick} fullWidth>
      Clear all selections
    </Button>
  );
};

const FindATimeOption = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      startIcon={() => <FindTime className="cw-w-4 cw-h-4" fill={fg_onEmphasis} />}
      variant="solid"
      size="small"
      onClick={onClick}
      sentiment="positive"
      fullWidth
    >
      Find time
    </Button>
  );
};

type SelectedOptionProps = {
  checked: boolean;
  isFixed?: boolean;
  onClick?: (calendarId: string) => void;
  option: CalendarSelectPeopleOption;
};
const SelectedOption = ({ checked, isFixed, onClick, option }: SelectedOptionProps) => {
  const disabled = !onClick;
  return (
    <MenuItem onClick={() => onClick?.(option.calendarId)} keepOpen disabled={disabled}>
      <div className="cw-flex cw-space-x-1 cw-items-center">
        <ColorCheckbox
          size="small"
          checked={checked}
          color={option.color}
          label={
            <>
              <AttendeeAvatar
                borderColor={option.color}
                hideTooltip
                profile={option.profile}
                size="medium"
              />
              <span className="cw-font-medium cw-ml-2 cw-mr-1">
                {getFullName(option.profile) || (isFixed ? "You" : "Unknown")}
              </span>
              <span className="cw-text-neutral-muted cw-body-sm">{option.calendarId}</span>
            </>
          }
        />
      </div>
    </MenuItem>
  );
};

type SuggestedOptionProps = {
  option: CalendarSelectPeopleOption;
  onClick: (calendarId: string) => void;
};
const SuggestedOption = ({ option, onClick }: SuggestedOptionProps) => {
  return (
    <MenuItem onClick={() => onClick(option.calendarId)} keepOpen>
      <div>
        <AttendeeAvatar size="medium" profile={option.profile} hideTooltip />
        <span className="cw-font-medium cw-ml-2 cw-mr-1">{getFullName(option.profile)}</span>
        <span className="cw-text-neutral-muted cw-body-sm">{option.calendarId}</span>
      </div>
    </MenuItem>
  );
};
